import React, { createContext, useState } from 'react';
import PropTypes from 'prop-types';

import useUserLog from 'src/hooks/useUserLog';
import useSessionContext from 'src/hooks/useSessionContext';
import { useLocation } from 'react-router-dom';
import useRestApiClient from 'src/hooks/useRestApiClient';

const initialState = {
  guidedTours: null,
  showGuidedTour: false,
  guidedTourSteps: [],
  newStepKey: 0
};

const GuidedTourContext = createContext({
  ...initialState
});

export const GuidedTourProvider = (props) => {
  const { children } = props;
  const { session } = useSessionContext();
  const user = session && session.user;
  const [guidedTours, setGuidedTours] = useState(initialState.guidedTours);
  const [showGuidedTour, setShowGuidedTour] = useState(
    initialState.showGuidedTour
  );
  const [guidedTourSteps, setGuidedTourSteps] = useState(
    initialState.guidedTourSteps
  );
  const { fetchUserLogs } = useUserLog();
  const location = useLocation();
  const client = useRestApiClient();
  const [newStepKey, setNewStepKey] = useState(initialState.newStepKey);
  const [userLastLog, setUserLastLog] = useState(1);

  const isNewUser = () => {
    return user && 'is_new' in user && +user.is_new === 1;
  };

  const sortGuidedTours = (arr, sortKey) => {
    return arr
      .sort((a, b) => {
        return +a.sort_key - +b.sort_key;
      })
      .filter((tour) => {
        return (
          +tour.sort_key >= sortKey && tour.data.href === location.pathname
        );
      })
      .map((tour) => tour.data);
  };

  const hasNewFeatures = () => {
    if (!userLastLog) {
      return false;
    }
    return +userLastLog.data < +guidedTours[guidedTours.length - 1].sort_key;
  };

  const showAllGuidedTours = () => {
    const lastLogKey = userLastLog ? userLastLog.data : 1;
    const stepKey = (isNewUser() && lastLogKey < newStepKey) ? lastLogKey : newStepKey;
    const sortedTour = sortGuidedTours(guidedTours, stepKey);
    if (lastLogKey < guidedTours.length) {
      setGuidedTourSteps(sortedTour);
    }
  };

  const showNewGuidedTours = () => {
    const lastLogKey = userLastLog ? userLastLog.data : guidedTours[0].sort_key;
    const stepKey =
      (isNewUser() && lastLogKey < newStepKey) ? lastLogKey : newStepKey;
    const sortedTour = sortGuidedTours(guidedTours, stepKey);
    if (lastLogKey < guidedTours.length) {
      setGuidedTourSteps(sortedTour);
    }
  };

  const fetchGuidedTours = async () => {
    const response = await client.get('/tours');
    const {
      data: { message: value }
    } = response;

    const userLogs = await fetchUserLogs();
    const sortedLogs = userLogs.sort((a, b) => {
      return parseInt(a.data, 10) - parseInt(b.data, 10);
    });
    const lastLog = sortedLogs[sortedLogs.length - 1];
    // sorted tour
    const tours = value.sort((a, b) => {
      return parseInt(a.data, 10) - parseInt(b.data, 10);
    });
    // filter by location
    const filteredTour = tours.filter((tour) => {
      return tour.data.href === location.pathname;
    });
    // new feature tours
    const newFeatureTours = filteredTour.filter((tour) => {
      return tour.is_new === true;
    });
    // tours based on logs
    const newTours = filteredTour.filter((tour) => {
      const lastLogKey = lastLog ? lastLog.data : newFeatureTours[0].sort_key;
      return tour.sort_key >= lastLogKey;
    });

    if (newTours && typeof newTours[0] !== 'undefined') {
      setNewStepKey(newTours[0].sort_key);
      setGuidedTourSteps(newTours.map((tour) => tour.data));
    }

    setGuidedTours(tours);
    setUserLastLog(lastLog);
    return tours;
  };

  const setDisplayGuidedTour = (displayValue) => {
    setShowGuidedTour(displayValue);
    if (!displayValue) {
      setGuidedTourSteps([]);
    }
  };

  const checkGuidedTour = () => {
    if (isNewUser() && userLastLog && guidedTours) {
      showAllGuidedTours();
    }

    if (user && guidedTours && !isNewUser()) {
      if (!userLastLog) showAllGuidedTours();
      if (userLastLog && hasNewFeatures()) {
        showNewGuidedTours();
      }
    }
  };

  return (
    <GuidedTourContext.Provider
      value={{
        guidedTours,
        showGuidedTour,
        guidedTourSteps,
        fetchGuidedTours,
        setDisplayGuidedTour,
        showAllGuidedTours,
        showNewGuidedTours,
        checkGuidedTour
      }}
      {...props}
    >
      {children}
    </GuidedTourContext.Provider>
  );
};

GuidedTourProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default GuidedTourContext;
